import { LibraryItem } from '@d-id/types';

export interface SimpleShowState {
    videoPageUrl?: string;
    editPageUrl?: string;
    reviewPageUrl?: string;
    isRendered?: boolean;
    role?: string;
    isEditor: boolean;
    shouldShowGoToVideo: boolean;
    shouldShowEdit: boolean;
    shouldShowComment: boolean;
}

export interface SimpleShowAction {
    text: string;
    url: string;
    eventName: 'video-edit' | 'video-preview';
}

export interface SimpleShowMenuOptions {
    shouldShowGoToVideo: boolean;
    shouldShowEdit: boolean;
    shouldShowComment: boolean;
    videoPageUrl?: string;
    editPageUrl?: string;
    reviewPageUrl?: string;
}

export const getSimpleShowState = (item: LibraryItem): SimpleShowState => {
    const { videoPageUrl, editPageUrl, reviewPageUrl, isRendered, role } = item.metadata || {};
    const isEditor = role === 'ROLE_PROJECT_EDITOR';

    return {
        videoPageUrl,
        editPageUrl,
        reviewPageUrl,
        isRendered,
        role,
        isEditor,
        shouldShowGoToVideo: isRendered && isEditor && !!videoPageUrl,
        shouldShowEdit: isEditor && !!editPageUrl,
        shouldShowComment: !!reviewPageUrl,
    };
};

export const getSimpleShowMenuOptions = (state: SimpleShowState): SimpleShowMenuOptions => {
    return {
        shouldShowGoToVideo: state.shouldShowGoToVideo,
        shouldShowEdit: state.shouldShowEdit,
        shouldShowComment: state.shouldShowComment,
        videoPageUrl: state.videoPageUrl,
        editPageUrl: state.editPageUrl,
        reviewPageUrl: state.reviewPageUrl,
    };
};

interface ActionDefinition {
    text: string;
    eventName: 'video-edit' | 'video-preview';
    getCondition: (state: SimpleShowState | SimpleShowMenuOptions) => boolean;
    getUrl: (state: SimpleShowState | SimpleShowMenuOptions) => string | undefined;
}

const actionDefinitions: ActionDefinition[] = [
    {
        text: 'Go to video page',
        eventName: 'video-preview',
        getCondition: state => state.shouldShowGoToVideo,
        getUrl: state => state.videoPageUrl,
    },
    {
        text: 'Edit project',
        eventName: 'video-edit',
        getCondition: state => state.shouldShowEdit,
        getUrl: state => state.editPageUrl,
    },
    {
        text: 'Comment on this video',
        eventName: 'video-preview',
        getCondition: state => state.shouldShowComment,
        getUrl: state => state.reviewPageUrl,
    },
];

interface ActionConfig {
    condition: boolean;
    action: SimpleShowAction;
}

const createActionConfigs = (state: SimpleShowState): ActionConfig[] =>
    actionDefinitions.map(def => ({
        condition: def.getCondition(state),
        action: {
            text: def.text,
            url: def.getUrl(state)!,
            eventName: def.eventName,
        },
    }));

export const getSimpleShowPrimaryAction = (state: SimpleShowState): SimpleShowAction | null => {
    const actions = createActionConfigs(state);
    return actions.find(({ condition }) => condition)?.action ?? null;
};

export const getSimpleShowMenuActions = (options: SimpleShowMenuOptions): SimpleShowAction[] => {
    return actionDefinitions
        .filter(def => def.getCondition(options) && def.getUrl(options))
        .map(def => ({
            text: def.text,
            url: def.getUrl(options)!,
            eventName: def.eventName,
        }));
};
